import { pie } from "../options/pie/pie";
import getEmphasis from "./get-emphasis";
import { lengthDataHandler } from "./get-length";
import { groupArrays } from "./group-arrays";
import { getTextStyle } from "./text-style";

type TProps = {
  data: Record<string, string>[][];
  colors: string[];
  primaryColor: string | undefined;
  type: string;
  payload:
    | {
        showAnnotation: boolean;
        annotationPosition: string;
        prefix: string;
        suffix: string;
        donutSize: string;
        fontFamily: string;
        showLegend: boolean;
        legendPosition: string;
      }
    | Record<string, unknown>;
};

type TSeriesData = {
  value: number | string;
  index: number;
  colors: string[];
  primaryColor: string | undefined;
  axisTitles: (string | number)[];
};

// Constants for static values
const OUTER_RADIUS = "80%";
const DEFAULT_COLOR = "rgba(0, 0, 0, 0.5)";
const DEFAULT_DONUT_SIZE = "70%";

// Helper function to create series data
function createSeriesData({
  value,
  index,
  colors,
  primaryColor,
  axisTitles,
}: TSeriesData) {
  const itemColor = colors[index] || primaryColor;
  return {
    value,
    itemStyle: { color: itemColor },
    name: axisTitles[index] || "",
    emphasis: getEmphasis(itemColor || ""),
  };
}

export const pieData = ({
  data,
  colors,
  primaryColor,
  type,
  payload,
}: TProps) => {
  const {
    showAnnotation,
    annotationPosition,
    prefix,
    suffix,
    fontFamily,
    donutSize,
    showLegend,
    legendPosition,
  } = payload;

  const annotationTextStyle = getTextStyle(payload, "annotation");

  // Work with copies of arrays to avoid mutating the original data
  const [seriesTitles = [], ...restData] = data.map((row) => [...row]);
  seriesTitles.shift();
  const grouped = groupArrays(restData);
  const [axisTitles = [], seriesData] = grouped;
  const titles = axisTitles.map((cell) => cell.value);

  const isMobile: boolean = window.innerWidth <= 768;
  if (!seriesData) return {};

  const legendSpacing: Record<string, number> = {};
  if (showLegend) {
    if (legendPosition === "bottom") {
      legendSpacing.bottom = 20;
    }
    if (legendPosition === "top") {
      legendSpacing.top = 20;
    }
  }

  const series = [
    {
      name: seriesTitles[0]?.value || "",
      type: "pie",
      data: seriesData.map((value, index) =>
        createSeriesData({
          value: value.value,
          index,
          colors,
          primaryColor,
          axisTitles: titles,
        }),
      ),
      radius:
        type === "donut"
          ? [`${donutSize || DEFAULT_DONUT_SIZE}%`, OUTER_RADIUS]
          : ["0", OUTER_RADIUS],
      avoidLabelOverlap: true,
      // universalTransition: true,
      animationDurationUpdate: 1000,
      label: {
        show: showAnnotation,
        position:
          annotationPosition === "outside" && !isMobile ? "outside" : "inside",
        formatter:
          annotationPosition === "outside" &&
          !isMobile &&
          (payload as Record<string, unknown>).showAnnotationLabel
            ? `{b}\n(${prefix || ""}{c}${suffix || ""})`
            : `${prefix || ""}{c}${suffix || ""}`,
        textStyle: {
          ...annotationTextStyle,
          fontFamily,
          color:
            payload.annotationPosition !== "inside"
              ? annotationTextStyle.color
              : undefined,
        },
      },
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: DEFAULT_COLOR,
        },
      },
      ...legendSpacing,
    },
  ];

  return {
    option: pie(payload, { series }),
    ...lengthDataHandler(titles, [series]),
  };
};
